import { Button } from "$components/button/button";
import { Chip } from "$components/chip";
import { Page } from "$components/page";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { triggerBrowserCsvDownload } from "$routes/_lib/bulk-operations/browser-utils";
import { useFacilityLocations } from "$routes/inventory/_lib/use-facility-locations";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import type { ReceiveStockBulkRowInput } from "elephant-web/gql";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  type ReceiveStockBulkUploadMutation,
  useReceiveStockBulkDownloadLazyQuery,
  useReceiveStockBulkUploadMutation,
  useValidateReceiveStockBulkUploadLazyQuery,
  type ValidateReceiveStockBulkUploadQuery,
} from "./_lib/__generated__/bulk-receive-operations.gql";
import { ReceiveBulkPreviewTable } from "./_lib/receive-bulk-preview-table";
import type {
  ReceiveBulkRowValidation,
  ResolvedSku,
} from "./_lib/row-sku-picker";

type ValidationResult = NonNullable<
  ValidateReceiveStockBulkUploadQuery["validateReceiveStockBulkUpload"]
>;
type BulkUploadResult =
  ReceiveStockBulkUploadMutation["receiveStockBulkUpload"];

type LocationResolution = { locationId: string | null; unknownName?: string };

function resolveLocation(
  locationName: string | null | undefined,
  locationByName: Map<string, string>,
): LocationResolution {
  const trimmed = locationName?.trim();
  if (!trimmed) {
    return { locationId: null };
  }
  const locationId = locationByName.get(trimmed.toLowerCase());
  return locationId
    ? { locationId }
    : { locationId: null, unknownName: trimmed };
}

const Detail: FC<{ label: React.ReactNode; value: number }> = ({
  label,
  value,
}) => (
  <Box marginRight={4}>
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="h6">{value}</Typography>
  </Box>
);

export const BulkReceivePage: FC = () => {
  const { facilityId } = useUser();
  const { formatMessage } = useIntl();

  const [downloadTemplate] = useReceiveStockBulkDownloadLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [validateUpload] = useValidateReceiveStockBulkUploadLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [commitUpload] = useReceiveStockBulkUploadMutation();

  const { data: facilityLocations } = useFacilityLocations();
  const locationByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const location of facilityLocations ?? []) {
      map.set(location.name.trim().toLowerCase(), location._id);
    }
    return map;
  }, [facilityLocations]);

  const [downloading, setDownloading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [resolutions, setResolutions] = useState<Record<number, ResolvedSku>>(
    {},
  );
  const [result, setResult] = useState<BulkUploadResult | null>(null);

  async function onDownloadTemplate() {
    setDownloading(true);
    try {
      const { data } = await downloadTemplate({ variables: { facilityId } });
      if (!data) {
        throw new Error("No template returned");
      }
      triggerBrowserCsvDownload(
        data.receiveStockBulkDownload.csv,
        "bulk-receive-template.csv",
      );
    } catch (_e) {
      toast.error(
        formatMessage({
          id: "inventory.bulkReceive.download.error",
          defaultMessage: "Could not download the template. Please try again.",
        }),
      );
    } finally {
      setDownloading(false);
    }
  }

  async function validateCsv(csv: string) {
    setValidating(true);
    setResult(null);
    setResolutions({});
    try {
      const { data } = await validateUpload({
        variables: { input: { facilityId, csv } },
      });
      const validated = data?.validateReceiveStockBulkUpload;
      if (!validated) {
        throw new Error("Validation returned no result");
      }
      setValidation(validated);
      // Prefill resolutions for rows the server already matched to a SKU.
      const prefilled: Record<number, ResolvedSku> = {};
      for (const row of validated.rows) {
        if (row.skuId) {
          prefilled[row.rowIndex] = {
            skuId: row.skuId,
            name: row.skuName ?? row.skuId,
          };
        }
      }
      setResolutions(prefilled);
    } catch (_e) {
      toast.error(
        formatMessage({
          id: "inventory.bulkReceive.validate.error",
          defaultMessage: "Could not validate the file. Please try again.",
        }),
      );
    } finally {
      setValidating(false);
    }
  }

  function onFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => validateCsv(e.target?.result as string);
    reader.readAsText(file);
  }

  function onResolve(rowIndex: number, sku: ResolvedSku | null) {
    setResolutions((current) => {
      const next = { ...current };
      if (sku) {
        next[rowIndex] = sku;
      } else {
        delete next[rowIndex];
      }
      return next;
    });
  }

  const rows = validation?.rows ?? [];

  const locationResolutions = useMemo(() => {
    const out: Record<number, LocationResolution> = {};
    for (const row of rows) {
      out[row.rowIndex] = resolveLocation(row.locationName, locationByName);
    }
    return out;
  }, [rows, locationByName]);

  const allRowsReady = useMemo(
    () =>
      rows.length > 0 &&
      rows.every(
        (row) =>
          !row.hasError &&
          !!resolutions[row.rowIndex] &&
          !locationResolutions[row.rowIndex]?.unknownName,
      ),
    [rows, resolutions, locationResolutions],
  );

  function buildRowInput(
    row: ReceiveBulkRowValidation,
  ): ReceiveStockBulkRowInput {
    const resolved = resolutions[row.rowIndex];
    return {
      skuId: resolved.skuId,
      quantity: row.quantity ?? 0,
      expiryDate: row.expiryDate ?? "",
      locationId: locationResolutions[row.rowIndex]?.locationId ?? null,
    };
  }

  async function onCommit() {
    setCommitting(true);
    try {
      const { data } = await commitUpload({
        variables: {
          input: { facilityId, rows: rows.map(buildRowInput) },
        },
      });
      if (data) {
        setResult(data.receiveStockBulkUpload);
        if (data.receiveStockBulkUpload.totalErrors === 0) {
          toast.success(
            formatMessage({
              id: "inventory.bulkReceive.commit.success",
              defaultMessage: "Stock received successfully.",
            }),
          );
        }
      }
    } catch (_e) {
      toast.error(
        formatMessage({
          id: "inventory.bulkReceive.commit.error",
          defaultMessage: "Could not receive stock. Please try again.",
        }),
      );
    } finally {
      setCommitting(false);
    }
  }

  return (
    <Page data-dense>
      <Typography variant="h4" gutterBottom>
        <FormattedMessage
          id="inventory.bulkReceive.heading"
          defaultMessage="Bulk receive"
        />
      </Typography>
      <Box marginBottom={2}>
        <Typography variant="h6" component="h2" gutterBottom>
          <FormattedMessage
            id="inventory.bulkReceive.steps.title"
            defaultMessage="How to receive stock"
          />
        </Typography>
        <ol style={{ marginTop: 0, paddingLeft: "1.25rem" }}>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="inventory.bulkReceive.steps.download"
                defaultMessage="Click <b>Download template</b> to get a CSV with the right columns. Start from a fresh template each time."
                values={{ b: (chunks) => <strong>{chunks}</strong> }}
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="inventory.bulkReceive.steps.fill"
                defaultMessage="Open it in Excel or Google Sheets and add one row per product you're receiving: product name, quantity, expiry date, and (optional) location."
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="inventory.bulkReceive.steps.save"
                defaultMessage="Save the file. Keep it as a CSV file."
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="inventory.bulkReceive.steps.upload"
                defaultMessage="Choose your saved file below to upload it."
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="inventory.bulkReceive.steps.resolve"
                defaultMessage="Check the preview. For any row marked <b>Find product</b>, search and pick the right product. Fix any rows showing an error."
                values={{ b: (chunks) => <strong>{chunks}</strong> }}
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="inventory.bulkReceive.steps.receive"
                defaultMessage="When every row has a product and there are no errors, click <b>Receive stock</b>."
                values={{ b: (chunks) => <strong>{chunks}</strong> }}
              />
            </Typography>
          </li>
        </ol>

        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            id="bulk-receive-rules-header"
            aria-controls="bulk-receive-rules-content"
          >
            <Typography variant="body2">
              <strong>
                <FormattedMessage
                  id="inventory.bulkReceive.rules.title"
                  defaultMessage="More details: how it works"
                />
              </strong>
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            id="bulk-receive-rules-content"
            aria-labelledby="bulk-receive-rules-header"
          >
            <Box
              component="ul"
              margin={0}
              paddingLeft="1.25rem"
              style={{ listStyleType: "disc" }}
            >
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="inventory.bulkReceive.rules.adds"
                    defaultMessage="Receiving <b>adds</b> stock. Each row increases the quantity for that product and expiry date, or creates it if it's new. It never overwrites or reduces stock."
                    values={{ b: (chunks) => <strong>{chunks}</strong> }}
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="inventory.bulkReceive.rules.columns"
                    defaultMessage="The file has 4 columns: <b>skuName</b>, <b>quantity</b>, <b>expiryDate</b>, <b>locationName</b>. Location is optional; leave it blank to use the facility's default location."
                    values={{ b: (chunks) => <strong>{chunks}</strong> }}
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="inventory.bulkReceive.rules.match"
                    defaultMessage="We match a row to a product by its <b>name</b>. If a name matches more than one product, we pick one for you — check it and change it in the preview if needed."
                    values={{ b: (chunks) => <strong>{chunks}</strong> }}
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="inventory.bulkReceive.rules.unmatched"
                    defaultMessage="If a name matches nothing, the row shows <b>Find product</b>. Search and pick the product — nothing is received for that row until you do."
                    values={{ b: (chunks) => <strong>{chunks}</strong> }}
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="inventory.bulkReceive.rules.fields"
                    defaultMessage="<b>quantity</b> must be a whole number greater than 0. <b>expiryDate</b> must be a date in the future."
                    values={{ b: (chunks) => <strong>{chunks}</strong> }}
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2" gutterBottom>
                  <FormattedMessage
                    id="inventory.bulkReceive.rules.location"
                    defaultMessage="<b>locationName</b> must be one of your facility's locations below, or leave it blank to use the default location."
                    values={{ b: (chunks) => <strong>{chunks}</strong> }}
                  />
                </Typography>
                {facilityLocations && facilityLocations.length > 0 && (
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    style={{ gap: 4 }}
                    marginTop={0.5}
                  >
                    {facilityLocations.map((location) => (
                      <Chip
                        key={location._id}
                        size="small"
                        label={location.name}
                      />
                    ))}
                  </Box>
                )}
              </li>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        marginTop={2}
        marginBottom={2}
        flexWrap="wrap"
      >
        <Button
          loading={downloading}
          onClick={onDownloadTemplate}
          data-testid="bulk-receive-download-template"
        >
          <FormattedMessage
            id="inventory.bulkReceive.downloadTemplate"
            defaultMessage="Download template"
          />
        </Button>

        <Box maxWidth="360px">
          <Typography variant="body2" gutterBottom>
            <FormattedMessage
              id="inventory.bulkReceive.upload.label"
              defaultMessage="Upload a CSV or Excel file:"
            />
          </Typography>
          <input
            data-testid="bulk-receive-file-input"
            type="file"
            accept=".csv,.xls,.xlsx"
            onChange={onFileChange}
          />
        </Box>
      </Box>

      {validating && (
        <Typography variant="body2">
          <FormattedMessage
            id="inventory.bulkReceive.validating"
            defaultMessage="Validating file…"
          />
        </Typography>
      )}

      {result && (
        <Box
          marginBottom={3}
          data-testid="bulk-receive-result"
          padding={2}
          bgcolor="grey.100"
          borderRadius={4}
        >
          <Typography variant="h6" gutterBottom>
            <FormattedMessage
              id="inventory.bulkReceive.result.heading"
              defaultMessage="Receive complete"
            />
          </Typography>
          <Box display="flex" flexWrap="wrap">
            <Detail
              label={
                <FormattedMessage
                  id="inventory.bulkReceive.result.totalSent"
                  defaultMessage="Rows sent"
                />
              }
              value={result.totalSent}
            />
            <Detail
              label={
                <FormattedMessage
                  id="inventory.bulkReceive.result.totalAdditions"
                  defaultMessage="New lots"
                />
              }
              value={result.totalAdditions}
            />
            <Detail
              label={
                <FormattedMessage
                  id="inventory.bulkReceive.result.totalUpdates"
                  defaultMessage="Updated lots"
                />
              }
              value={result.totalUpdates}
            />
            <Detail
              label={
                <FormattedMessage
                  id="inventory.bulkReceive.result.totalErrors"
                  defaultMessage="Errors"
                />
              }
              value={result.totalErrors}
            />
          </Box>
          {result.errors.length > 0 && (
            <ul>
              {result.errors.map((error) => (
                <li key={error.id}>
                  <Typography variant="body2" color="error">
                    {error.errorMessage}
                  </Typography>
                </li>
              ))}
            </ul>
          )}
        </Box>
      )}

      {!result && rows.length > 0 && (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            marginBottom={2}
          >
            <Box display="flex" flexWrap="wrap">
              <Detail
                label={
                  <FormattedMessage
                    id="inventory.bulkReceive.summary.totalSent"
                    defaultMessage="Rows"
                  />
                }
                value={validation?.totalSent ?? 0}
              />
              <Detail
                label={
                  <FormattedMessage
                    id="inventory.bulkReceive.summary.matched"
                    defaultMessage="Matched"
                  />
                }
                value={validation?.totalMatched ?? 0}
              />
              <Detail
                label={
                  <FormattedMessage
                    id="inventory.bulkReceive.summary.errors"
                    defaultMessage="Errors"
                  />
                }
                value={validation?.totalErrors ?? 0}
              />
            </Box>
            <Button
              color="primary"
              variant="contained"
              loading={committing}
              disabled={!allRowsReady}
              onClick={onCommit}
              data-testid="bulk-receive-commit"
            >
              <FormattedMessage
                id="inventory.bulkReceive.commit"
                defaultMessage="Receive stock"
              />
            </Button>
          </Box>

          {!allRowsReady && (
            <Box marginBottom={2}>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage
                  id="inventory.bulkReceive.commit.blocked"
                  defaultMessage="Confirm a product for every row and clear all errors before you can receive."
                />
              </Typography>
            </Box>
          )}

          <ReceiveBulkPreviewTable
            rows={rows}
            resolutions={resolutions}
            onResolve={onResolve}
            unknownLocations={Object.fromEntries(
              rows.map((row) => [
                row.rowIndex,
                locationResolutions[row.rowIndex]?.unknownName,
              ]),
            )}
          />
        </>
      )}
    </Page>
  );
};
